import { Calendar, ChevronRight, Receipt, UserPlus, Users } from 'lucide-react';
import type {
  GlobalSearchActionHit,
  GlobalSearchAppointmentHit,
  GlobalSearchHit,
  GlobalSearchInvoiceHit,
  GlobalSearchPatientHit,
  GlobalSearchRecentHit,
} from '../domain/global-search.types';
import {
  GlobalSearchIdleState,
  GlobalSearchMinCharsHint,
  GlobalSearchNoResults,
  GlobalSearchSkeleton,
} from './GlobalSearchEmptyState';
import {
  GlobalSearchAppointmentRow,
  GlobalSearchInvoiceRow,
  GlobalSearchPatientRow,
  GlobalSearchSectionHeader,
  GlobalSearchViewAllRow,
} from './GlobalSearchResultRows';
import { Button } from '@/shared/ui/Button';
import { cn } from '@/shared/lib/utils';

const COMMAND_ICONS = {
  userPlus: UserPlus,
  users: Users,
  calendar: Calendar,
  receipt: Receipt,
} as const;

export interface GlobalSearchResultsBodyProps {
  isIdle: boolean;
  isBelowMinChars: boolean;
  idleRecents: GlobalSearchRecentHit[];
  idleActions: GlobalSearchActionHit[];
  highlight: number;
  onHighlight: (index: number) => void;
  onSelect: (href: string) => void;
  onClearRecents: () => void;
  listId: string;
  showLoading: boolean;
  waitingForDebounce: boolean;
  isError: boolean;
  showNoResults: boolean;
  debounced: string;
  query: string;
  patientHits: GlobalSearchPatientHit[];
  appointmentHits: GlobalSearchAppointmentHit[];
  invoiceHits: GlobalSearchInvoiceHit[];
  hits: GlobalSearchHit[];
}

/** Renders idle / loading / error / results panes for the palette. */
export function GlobalSearchResultsBody({
  isIdle,
  isBelowMinChars,
  idleRecents,
  idleActions,
  highlight,
  onHighlight,
  onSelect,
  onClearRecents,
  listId,
  showLoading,
  waitingForDebounce,
  isError,
  showNoResults,
  debounced,
  query,
  patientHits,
  appointmentHits,
  invoiceHits,
  hits,
}: GlobalSearchResultsBodyProps): JSX.Element {
  if (isIdle) {
    return (
      <GlobalSearchIdleState
        recents={idleRecents}
        actions={idleActions}
        highlightIndex={highlight}
        onHighlight={onHighlight}
        onSelect={onSelect}
        onClearRecents={onClearRecents}
        listId={listId}
      />
    );
  }

  if (isBelowMinChars) {
    return <GlobalSearchMinCharsHint query={query} />;
  }

  if (showLoading || waitingForDebounce) {
    return <GlobalSearchSkeleton />;
  }

  if (isError) {
    return (
      <div className="mx-3.5 my-4 rounded-xl bg-coral-50 px-4 py-6 text-center text-[13px] text-coral-700">
        Could not load search results. Try again.
      </div>
    );
  }

  if (showNoResults) {
    return (
      <GlobalSearchNoResults
        query={debounced}
        onRegisterPatient={() => onSelect('/patients/new')}
      />
    );
  }

  const commandHits = hits.filter((h): h is GlobalSearchActionHit => h.kind === 'action');
  if (commandHits.length > 0 && patientHits.length === 0 && appointmentHits.length === 0 && invoiceHits.length === 0) {
    return (
      <div
        id={listId}
        role="listbox"
        aria-label="Commands"
        className="max-h-[min(62vh,560px)] space-y-0.5 overflow-y-auto nice-scroll p-2"
      >
        <GlobalSearchSectionHeader
          icon={<Users className="size-3.5" aria-hidden />}
          label="Commands"
          count={commandHits.length}
          query={debounced}
        />
        {commandHits.map((action, index) => {
          const Icon = COMMAND_ICONS[action.icon] ?? Users;
          const active = highlight === index;
          return (
            <Button
              key={action.id}
              type="button"
              variant="ghost"
              role="option"
              id={`global-search-hit-${index}`}
              aria-selected={active}
              className={cn(
                'global-search-option h-auto w-full justify-start gap-2.5 rounded-xl px-2.5 py-2.5 text-left',
                active && 'global-search-option-active',
              )}
              onMouseEnter={() => onHighlight(index)}
              onClick={() => onSelect(action.href)}
            >
              <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                <Icon className="size-3.5" aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold text-ink-900">{action.label}</span>
                <span className="block text-[11px] text-ink-500">{action.description}</span>
              </span>
              <ChevronRight className="size-3.5 shrink-0 text-ink-300" aria-hidden />
            </Button>
          );
        })}
      </div>
    );
  }

  const appointmentStart = patientHits.length;
  const invoiceStart = appointmentStart + appointmentHits.length;

  const viewAllAppointmentIndex =
    appointmentHits.length > 0
      ? hits.findIndex((h) => h.kind === 'viewAllAppointments')
      : -1;
  const viewAllInvoiceIndex =
    invoiceHits.length > 0 ? hits.findIndex((h) => h.kind === 'viewAllInvoices') : -1;

  const viewAllAppointmentHit =
    viewAllAppointmentIndex >= 0 ? hits[viewAllAppointmentIndex] : undefined;
  const viewAllInvoiceHit = viewAllInvoiceIndex >= 0 ? hits[viewAllInvoiceIndex] : undefined;

  return (
    <div
      id={listId}
      role="listbox"
      aria-label="Search results"
      className="max-h-[min(62vh,560px)] overflow-y-auto nice-scroll"
    >
      {patientHits.length > 0 ? (
        <section>
          <GlobalSearchSectionHeader
            icon={<Users className="size-3.5" aria-hidden />}
            label="Patients"
            count={patientHits.length}
            query={debounced}
          />
          <div className="p-1.5">
            {patientHits.map((hit, i) => (
              <GlobalSearchPatientRow
                key={hit.id}
                hit={hit}
                index={i}
                active={highlight === i}
                onHighlight={onHighlight}
                onSelect={onSelect}
              />
            ))}
          </div>
        </section>
      ) : null}

      {appointmentHits.length > 0 ? (
        <section>
          <GlobalSearchSectionHeader
            icon={<Calendar className="size-3.5" aria-hidden />}
            label="Appointments"
            count={appointmentHits.length}
            query={debounced}
          />
          <div className="p-1.5">
            {appointmentHits.map((hit, i) => (
              <GlobalSearchAppointmentRow
                key={hit.id}
                hit={hit}
                index={appointmentStart + i}
                active={highlight === appointmentStart + i}
                onHighlight={onHighlight}
                onSelect={onSelect}
              />
            ))}
            {viewAllAppointmentHit?.kind === 'viewAllAppointments' ? (
              <GlobalSearchViewAllRow
                hit={viewAllAppointmentHit}
                index={viewAllAppointmentIndex}
                active={highlight === viewAllAppointmentIndex}
                onHighlight={onHighlight}
                onSelect={onSelect}
              />
            ) : null}
          </div>
        </section>
      ) : null}

      {invoiceHits.length > 0 ? (
        <section>
          <GlobalSearchSectionHeader
            icon={<Receipt className="size-3.5" aria-hidden />}
            label="Invoices"
            count={invoiceHits.length}
            query={debounced}
          />
          <div className="p-1.5">
            {invoiceHits.map((hit, i) => (
              <GlobalSearchInvoiceRow
                key={hit.id}
                hit={hit}
                index={invoiceStart + i}
                active={highlight === invoiceStart + i}
                onHighlight={onHighlight}
                onSelect={onSelect}
              />
            ))}
            {viewAllInvoiceHit?.kind === 'viewAllInvoices' ? (
              <GlobalSearchViewAllRow
                hit={viewAllInvoiceHit}
                index={viewAllInvoiceIndex}
                active={highlight === viewAllInvoiceIndex}
                onHighlight={onHighlight}
                onSelect={onSelect}
              />
            ) : null}
          </div>
        </section>
      ) : null}
    </div>
  );
}
